import {
  // -- GET (one)
  // GET_FLOW_GROUP_SUCCESS,
  // GET_FLOW_GROUP_FAILURE,
  // -- GET (list)
  GET_FLOW_GROUPS_SUCCESS,
  GET_FLOW_GROUPS_FAILURE,
  // -- UPDATE
  UPDATE_FLOW_GROUP_SUCCESS,
  UPDATE_FLOW_GROUP_FAILURE,
  // -- CREATE
  CREATE_FLOW_GROUP_SUCCESS,
  CREATE_FLOW_GROUP_FAILURE,
  // -- DELETE
  DELETE_FLOW_GROUP_SUCCESS,
  DELETE_FLOW_GROUP_FAILURE,
  // -- SET FLOW GROUP ELEMENTS
  SET_CURRENT_FLOW_GROUP,
  CACHE_UPDATE_FLOW_GROUP_START_ACTION,
  CACHE_UPDATE_FLOW_GROUP_CONDITION,
  CACHE_UPDATE_FLOW_GROUP_TASK,
  CACHE_UPDATE_FLOW_GROUP_TASK_ASSIGN,
  // -- UPDATE FLOW GROUP ELEMENTS DEPENDENCIES
  CACHE_CREATE_FLOW_CONDITION_CHECK,
  CACHE_DELETE_FLOW_CONDITION_CHECK,
  CACHE_CREATE_FLOW_TASK_INTERACTION_CHECK,
  CACHE_DELETE_FLOW_TASK_INTERACTION_CHECK,
  CACHE_CREATE_FLOW_TASK_INTERACTION_RESOURCE,
  CACHE_UPDATE_FLOW_TASK_INTERACTION_RESOURCE,
  CACHE_DELETE_FLOW_TASK_INTERACTION_RESOURCE,
  CACHE_CREATE_FLOW_TASK_INTERACTION_VARIABLE,
  CACHE_UPDATE_FLOW_TASK_INTERACTION_VARIABLE,
  CACHE_DELETE_FLOW_TASK_INTERACTION_VARIABLE
} from '../../actions'
import { Logger, LoggerTypes } from '../../utils'

/**
 * For performance reasons (ISSUE:3475) the below methods have been made to create a REDUX state (cache)
 * which prevent from calling the back-end each time a small change (like task update) is made.
 *
 * This state map contains:
 * {
 *  items: An array of flow groups [ {}, {}, {} ... ]
 *  flowGroupConditions: A map of flow group identifiers pointing to another map with their flow conditions
 *  flowGroupTasks: A map of flow group identifiers pointing to another map with their flow tasks
 * }
 *
 * items is populated only one time when the app is querying the scenario's flowgroups from the back-end.
 * flowGroupConditions & flowGroupTasks are build based on the content of items.
 *
 * When the app is updating a condition or a task, some changes can have a direct impact on the flow group' XML
 * which is used to render the BPMN designer. To keep the user interface coherent below is how the changes are
 * propagated from front to back-end
 *
 * 1) When the user updates a BPMN element (condition or task selected) the data are first sent to the back-end.
 * 2) The back-end updates the related object and updates the XML definition of the flow group
 * 3) The result if then propagated in this REDUX state, to override the element in the corresponding cache map
 * (flowGroupConditions or flowGroupTasks).
 * 4) Then the BPMN designer is notified of this change and updates the related element to update the view.
 * 5) The flow group's XML value is updated whith the BPMN designer and sent to this REDUX state, to override
 * the flow group in the `items` array
 *
 * !IMPORTANT!
 * The flow group contains in its structure, the task which has been updated in the back-end. Because it has not been
 * recovered from the back-end this structure is now obsolete and should not be used anymore for any rendering process.
 *
 * It's very important to maintain the coherence which exists between items, flowGroupConditions and flowGroupTasks in
 * this REDUX state.
 *
 * The coherence exists because the app is using items for the flow group tabs only and the BPMN selected elements with the
 * flowGroupConditions and flowGroupTasks. These two maps are build only when the flow group or all flow groups are
 * collected from the back-end which correspond to the selection of a new scenario or using the save button of the
 * BPMN designer.
 *
 */

// Returns a map of all start actions from all flows of a flow group
const _getFlowGroupStartActions = (flowGroup) => {
  let allStartActions = new Map([])
  ;(flowGroup?.flows || []).forEach((flow) => {
    // TODO Update the datamodel to have the name inside the startAction
    // Invalid flows have no startAction
    allStartActions.set(flow?.startAction?.element_id, flow)
  })

  return allStartActions
}

/* Returns a map of all conditions from all flows of a flow group.
 *
 * The result is structured as below:
 *
 * [ Map
 *   {
 *     key: 'condition.element_id',
 *     value: the task object with an array of all its instances
 *   },
 * ]
 *
 * Note that the key of the task instances map is the BPMN element id which allows the UI to retrieve
 * the appropriate taskInstance based on the selected BPMN element.
 */

const _getFlowGroupConditions = (flowGroup) => {
  let allConditions = new Map([])
  ;(flowGroup?.flows || []).map((flow) =>
    (flow?.conditions || []).forEach((el) => {
      // The key is the BPMN element_id
      allConditions.set(el.element_id, el)
    })
  )

  return allConditions
}

/* Returns a map of all task and instances from all flows of a flow group.
 *
 * The result is structured as below:
 *
 * {
 *  tasks: [ Map
 *    {
 *      key: 'task.id',
 *      value: the task object with an array of all its instances
 *    },
 *  ],
 *  instances: [ Map
 *    {
 *      key: 'taskInstance.element_id',
 *      value: the task instance object
 *    },
 *    ...
 *  ]
 * }
 *
 * Note that the key of the task instances map is the BPMN element id which allows the UI to retrieve
 * the appropriate taskInstance based on the selected BPMN element.
 */
const _getFlowGroupTasks = (flowGroup) => {
  let allTasks = new Map([])
  let allTaskInstances = new Map([])
  let taskInstanceList = []
  ;(flowGroup?.flows || []).map((flow) =>
    (flow?.taskInstances || []).forEach((el) => {
      // The key is the BPMN element_id
      allTaskInstances.set(el.element_id, el)

      taskInstanceList = allTasks.get(el.task.id)?.instances || []
      taskInstanceList.push(el)
      allTasks.set(el.task.id, {
        ...el.task,
        instances: taskInstanceList
      })
    })
  )

  return {
    // tasks: [...allTasks.values()],
    tasks: allTasks,
    instances: allTaskInstances
  }
}

const _cacheUpdateFlowGroupStartAction = (state = {}, flowGroup, start) => {
  state?.flowGroupStartActions?.get(flowGroup.id)?.set(start.startAction.element_id, start)

  return {
    ...state
  }
}

const _cacheUpdateFlowGroupCondition = (state = {}, flowGroup, condition) => {
  state?.flowGroupConditions?.get(flowGroup.id)?.set(condition.element_id, condition)

  return {
    ...state
  }
}

/* Update a task instance object and it's related task definition in the current state.
 *
 * According to the result which is provided by the _getFlowGroupTasks method,
 * this one is updating both 'task' and 'instances' maps to be consistent.
 */
const _cacheUpdateFlowGroupTaskInstance = (state = {}, flowGroup, taskInstance) => {
  // Update the task instance in the instance list of its flowGroup
  const flowGroupTasks = state?.flowGroupTasks?.get(flowGroup.id)
  flowGroupTasks?.instances?.set(taskInstance.element_id, taskInstance)

  // Get the instance's task in the task section
  const taskObject = flowGroupTasks?.tasks?.get(taskInstance.task.id)

  // Removes the task instance which has been updated from the list
  taskObject.instances = taskObject.instances.filter((_taskInstance) => _taskInstance.id !== taskInstance.id)
  // Update the task in all instances on the current task of the current flowGroup
  taskObject.instances.forEach((el) => {
    el.task = {
      ...taskInstance.task
    }
  })
  // Update the list with the new task instance
  taskObject.instances.push(taskInstance)

  // Update all other tasks within all other groups if the current instance's task is shared with multiple instances
  state?.flowGroupTasks?.forEach((_flowGroupTasks, _flowGroupId) => {
    // Update the instance's task in the task section
    _flowGroupTasks?.instances?.forEach((_instances) => {
      if (_instances.task.id === taskInstance.task.id) {
        _instances.task = taskInstance.task
      }
    })

    // Update the task object of all instances of all flowGroups
    const flowGroupTask = _flowGroupTasks?.tasks?.get(taskInstance.task.id) || null
    if (flowGroupTask !== null) {
      const taskUpdated = {
        ...taskInstance.task,
        instances: _flowGroupId === flowGroup.id ? [...taskObject.instances] : [...flowGroupTask.instances]
      }

      _flowGroupTasks?.tasks?.set(taskInstance.task.id, taskUpdated)
    }
  })

  // flowGroupTasks?.tasks?.set(taskInstance.task.id, {
  //   ...taskInstance.task,
  //   instances: [...taskObject.instances]
  // })

  return {
    ...state
  }
}

const _cacheUpdateFlowGroupTaskAssign = (state = {}, flowGroup, taskInstance) => {
  const currentFlowGroupTasks = state.flowGroupTasks.get(flowGroup.id)
  // Update the instance in the instances section
  currentFlowGroupTasks.instances.set(taskInstance.element_id, taskInstance) // => Map [ {ti}, {ti}, {ti} ]]

  // Update the tasks which have been impacted by assignment in the task section
  let orphanTaskId = null
  let taskFound = false // The task can come from another flow group so it's not yet defined
  currentFlowGroupTasks.tasks.forEach((el) => {
    // Update the list of instances of the currentTask if its the one which has been assigned
    if (el.id === taskInstance.task.id) {
      el.instances.push(taskInstance)
      taskFound = true
    } else {
      // Removes the task instance from the currentTask if exists in its list
      el.instances = el.instances.filter((_taskInstance) => _taskInstance.id !== taskInstance.id)
    }
    // If the task has no more instances, it has to be removed from the map
    if (el.instances.length === 0) {
      orphanTaskId = el.id
    }
  })

  if (!taskFound) {
    // If the task assigned to the instance comes from another flow group,
    // the current one does not contain this task. So we add it now because
    // it already contains the instance
    currentFlowGroupTasks.tasks.set(taskInstance.task.id, {
      ...taskInstance.task,
      instances: [taskInstance]
    })
  }
  if (orphanTaskId !== null) {
    currentFlowGroupTasks.tasks.delete(orphanTaskId)
  }

  return {
    ...state
  }
}

const _addFlowGroupConditionCheck = (state = {}, flowGroup, cid, check) => {
  state.flowGroupConditions.get(flowGroup.id).forEach((el) => {
    if (el.id === cid) {
      el.checks.push(check)
    }
  })

  return {
    ...state
  }
}

const _addFlowGroupTaskInstanceCheck = (state = {}, flowGroup, tid, check) => {
  state.flowGroupTasks.get(flowGroup.id).instances.forEach((el) => {
    if (el.id === tid) {
      el.checks.push(check)
    }
  })

  return {
    ...state
  }
}

const _addFlowGroupTaskInstanceResourceInteraction = (state = {}, flowGroup, tid, interactions) => {
  state.flowGroupTasks.get(flowGroup.id).instances.forEach((el) => {
    if (el.id === tid) {
      el.resource_interactions = [...interactions]
    }
  })

  return {
    ...state
  }
}

const _addFlowGroupTaskInstanceVariableInteraction = (state = {}, flowGroup, tid, interactions) => {
  state.flowGroupTasks.get(flowGroup.id).instances.forEach((el) => {
    if (el.id === tid) {
      el.variable_interactions = [...interactions]
    }
  })

  return {
    ...state
  }
}

const _updateFlowGroupTaskInstanceResourceInteraction = (state = {}, flowGroup, tid, interaction) => {
  state.flowGroupTasks.get(flowGroup.id).instances.forEach((el) => {
    if (el.id === tid) {
      el.resource_interactions = el.resource_interactions.filter((_interaction) => _interaction.id !== interaction.id)
      el.resource_interactions.push(interaction)
    }
  })
  return {
    ...state
  }
}

const _updateFlowGroupTaskInstanceVariableInteraction = (state = {}, flowGroup, tid, interaction) => {
  state.flowGroupTasks.get(flowGroup.id).instances.forEach((el) => {
    if (el.id === tid) {
      el.variable_interactions = el.variable_interactions.filter((_interaction) => {
        return (
          _interaction.variable_id !== interaction.variable_id ||
          (_interaction.variable_id === interaction.variable_id && _interaction.type !== interaction.type)
        )
      })
      el.variable_interactions.push(interaction)
    }
  })

  return {
    ...state
  }
}

const _removeFlowGroupConditionCheck = (state = {}, flowGroup, cid, checkId) => {
  state.flowGroupConditions.get(flowGroup.id).forEach((el) => {
    if (el.id === cid) {
      el.checks = el.checks.filter((check) => check.id !== checkId)
    }
  })

  return {
    ...state
  }
}

const _removeFlowGroupTaskInstanceCheck = (state = {}, flowGroup, tid, checkId) => {
  state.flowGroupTasks.get(flowGroup.id).instances.forEach((el) => {
    if (el.id === tid) {
      el.checks = el.checks.filter((check) => check.id !== checkId)
    }
  })

  return {
    ...state
  }
}

const _removeFlowGroupTaskInstanceResourceInteraction = (state = {}, flowGroup, tid, resourceId) => {
  state.flowGroupTasks.get(flowGroup.id).instances.forEach((el) => {
    if (el.id === tid) {
      el.resource_interactions = el.resource_interactions.filter(
        (interaction) => interaction.resource_id !== resourceId
      )
    }
  })

  return {
    ...state
  }
}

const _removeFlowGroupTaskInstanceVariableInteraction = (state = {}, flowGroup, tid, interaction) => {
  state.flowGroupTasks.get(flowGroup.id).instances.forEach((el) => {
    if (el.id === tid) {
      el.variable_interactions = el.variable_interactions.filter((_interaction) => {
        return (
          _interaction.variable_id !== interaction.variable_id ||
          (_interaction.variable_id === interaction.variable_id && _interaction.type !== interaction.type)
        )
      })
    }
  })

  return {
    ...state
  }
}

const _addFlowGroupInCache = (state = {}, flowGroup) => {
  // Put the flow group definition in the cache and index its conditions & tasks instances
  state.items.push(flowGroup)
  state.flowGroupStartActions.set(flowGroup.id, _getFlowGroupStartActions(flowGroup))
  state.flowGroupConditions.set(flowGroup.id, _getFlowGroupConditions(flowGroup))
  state.flowGroupTasks.set(flowGroup.id, _getFlowGroupTasks(flowGroup))

  return {
    ...state
  }
}

// Update a flow group in the REDUX state
const _updateFlowGroupInCache = (state = {}, flowGroup, build_flow_group_cache = false) => {
  // Rebuild the items and replace the flowGroup by it's new value
  let items = []
  state.items.forEach((el) => {
    if (el.id === flowGroup.id) {
      el = flowGroup
    }
    items.push(el)
  })

  if (build_flow_group_cache) {
    state.flowGroupStartActions.set(flowGroup.id, _getFlowGroupStartActions(flowGroup))
    state.flowGroupConditions.set(flowGroup.id, _getFlowGroupConditions(flowGroup))
    state.flowGroupTasks.set(flowGroup.id, _getFlowGroupTasks(flowGroup))
  }

  return {
    ...state,
    items
  }
}

const _deleteFlowGroupFromCache = (state = {}, flowGroupId) => {
  // Delete the flow group from the cache
  // let items = []
  // state.items = state.items.forEach((el) => items.push(el))
  state.items = state.items.filter((obj) => obj.id !== flowGroupId)

  // Delete the flow group's conditions & tasks from the cache
  state.flowGroupConditions.delete(flowGroupId)
  state.flowGroupTasks.delete(flowGroupId)

  return state
}

// Returns a map of all flow groups which contains a map of all start actions from their flows
const _getAllStartActions = (flowGroups) => {
  let allStarts = new Map([])
  flowGroups.forEach((flowGroup) => {
    allStarts.set(flowGroup.id, _getFlowGroupStartActions(flowGroup))
  })
  return allStarts
}

const _getAllConditions = (flowGroups) => {
  let allConditions = new Map([])
  flowGroups.forEach((flowGroup) => {
    allConditions.set(flowGroup.id, _getFlowGroupConditions(flowGroup))
  })
  return allConditions
}

// Returns a map of all flow groups which contains a map of all task instances from their flows
const _getAllTasks = (flowGroups) => {
  let allTasks = new Map([])
  flowGroups.forEach((flowGroup) => {
    allTasks.set(flowGroup.id, _getFlowGroupTasks(flowGroup))
  })
  return allTasks
}

export const flowGroups = (state = {}, action) => {
  switch (action.type) {
    // -- GET FLOW GROUPS (tabs)
    case GET_FLOW_GROUPS_FAILURE: {
      return { ...state, error: action.data }
    }
    case GET_FLOW_GROUPS_SUCCESS: {
      const items = action?.data
      const currentFlowGroup = items.length === 0 ? null : items[0]
      // Initialize the starts, conditions & taskInstances of all flow groups
      const flowGroupStartActions = _getAllStartActions(items)
      const flowGroupConditions = _getAllConditions(items)
      const flowGroupTasks = _getAllTasks(items)

      const newState = {
        ...state,
        items,
        currentFlowGroup,
        flowGroupStartActions,
        flowGroupConditions,
        flowGroupTasks
      }

      Logger.trace(LoggerTypes.REDUCER, '[REDUCER] flowGroups.GET_FLOW_GROUPS_SUCCESS()', {
        oldState: state,
        action,
        newState
      })

      return newState
    }

    // -- GET A FLOW GROUP (one tab)
    // case GET_FLOW_GROUP_FAILURE: {
    //   return { ...state, error: action.data }
    // }
    // case GET_FLOW_GROUP_SUCCESS: {
    //   const newState = {
    //     ..._updateFlowGroupInCache(state, action.data, true),
    //     currentFlowGroup: action.data
    //   }

    //   Logger.trace(LoggerTypes.REDUCER, '[REDUCER] flowGroups.GET_FLOW_GROUP_SUCCESS()', {
    //     oldState: state,
    //     action,
    //     newState
    //   })

    //   return newState
    // }

    // -- CREATE A FLOW GROUP
    case CREATE_FLOW_GROUP_FAILURE: {
      return { ...state, error: action.data }
    }
    case CREATE_FLOW_GROUP_SUCCESS: {
      const newState = {
        ..._addFlowGroupInCache(state, action.data),
        currentFlowGroup: action.data
      }

      Logger.trace(LoggerTypes.REDUCER, '[REDUCER] flowGroups.CREATE_FLOW_GROUP_SUCCESS()', {
        oldState: state,
        action,
        newState
      })

      return newState
    }

    // -- UPDATE A FLOW GROUP
    case UPDATE_FLOW_GROUP_FAILURE: {
      return { ...state, error: action.data }
    }
    case UPDATE_FLOW_GROUP_SUCCESS: {
      const newState = {
        ..._updateFlowGroupInCache(state, action.data, true),
        currentFlowGroup: action.data
      }

      Logger.trace(LoggerTypes.REDUCER, '[REDUCER] flowGroups.UPDATE_FLOW_GROUP_SUCCESS()', {
        oldState: state,
        action,
        newState
      })

      return newState
    }

    // -- DELETE A FLOW GROUP
    case DELETE_FLOW_GROUP_FAILURE: {
      return { ...state, error: action.data }
    }
    case DELETE_FLOW_GROUP_SUCCESS: {
      const newState = {
        ..._deleteFlowGroupFromCache(state, action.data),
        currentFlowGroup: null
      }

      Logger.trace(LoggerTypes.REDUCER, '[REDUCER] flowGroups.DELETE_FLOW_GROUP_SUCCESS()', {
        oldState: state,
        action,
        newState
      })

      return newState
    }

    // -- SET CURRENT FLOW GROUP
    case SET_CURRENT_FLOW_GROUP: {
      // attribute "build_flow_group_cache" is false here because the currentFlowGroup can contains tasks definitions
      // which have been updated in the back-end and not yet synchronized
      const newState = {
        ..._updateFlowGroupInCache(state, action.data, false),
        currentFlowGroup: action.data
      }

      Logger.trace(LoggerTypes.REDUCER, '[REDUCER] flowGroups.SET_CURRENT_FLOW_GROUP()', {
        oldState: state,
        action,
        newState
      })

      return newState
    }

    case CACHE_UPDATE_FLOW_GROUP_START_ACTION: {
      const newState = _cacheUpdateFlowGroupStartAction(state, action.data.flowGroup, action.data.startAction)

      Logger.trace(LoggerTypes.REDUCER, '[REDUCER] flowGroups.CACHE_UPDATE_FLOW_GROUP_START_ACTION()', {
        oldState: state,
        action,
        newState
      })

      return newState
    }

    case CACHE_UPDATE_FLOW_GROUP_CONDITION: {
      const newState = _cacheUpdateFlowGroupCondition(state, action.data.flowGroup, action.data.condition)

      Logger.trace(LoggerTypes.REDUCER, '[REDUCER] flowGroups.CACHE_UPDATE_FLOW_GROUP_CONDITION()', {
        oldState: state,
        action,
        newState
      })

      return newState
    }

    case CACHE_UPDATE_FLOW_GROUP_TASK: {
      const newState = _cacheUpdateFlowGroupTaskInstance(state, action.data.flowGroup, action.data.taskInstance)

      Logger.trace(LoggerTypes.REDUCER, '[REDUCER] flowGroups.CACHE_UPDATE_FLOW_GROUP_TASK()', {
        oldState: state,
        action,
        newState
      })

      return newState
    }

    case CACHE_UPDATE_FLOW_GROUP_TASK_ASSIGN: {
      const newState = _cacheUpdateFlowGroupTaskAssign(state, action.data.flowGroup, action.data.taskInstance)

      Logger.trace(LoggerTypes.REDUCER, '[REDUCER] flowGroups.CACHE_UPDATE_FLOW_GROUP_TASK_ASSIGN()', {
        oldState: state,
        action,
        newState
      })

      return newState
    }

    case CACHE_CREATE_FLOW_CONDITION_CHECK: {
      const newState = _addFlowGroupConditionCheck(state, action.data.flowGroup, action.data.cid, action.data.check)

      Logger.trace(LoggerTypes.REDUCER, '[REDUCER] flowGroups.CACHE_CREATE_FLOW_CONDITION_CHECK()', {
        oldState: state,
        action,
        newState
      })

      return newState
    }

    case CACHE_DELETE_FLOW_CONDITION_CHECK: {
      const newState = _removeFlowGroupConditionCheck(
        state,
        action.data.flowGroup,
        action.data.cid,
        action.data.checkId
      )

      Logger.trace(LoggerTypes.REDUCER, '[REDUCER] flowGroups.CACHE_DELETE_FLOW_CONDITION_CHECK()', {
        oldState: state,
        action,
        newState
      })

      return {
        ...newState
      }
    }

    case CACHE_CREATE_FLOW_TASK_INTERACTION_CHECK: {
      const newState = _addFlowGroupTaskInstanceCheck(state, action.data.flowGroup, action.data.tid, action.data.check)

      Logger.trace(LoggerTypes.REDUCER, '[REDUCER] flowGroups.CACHE_CREATE_FLOW_TASK_INTERACTION_CHECK()', {
        oldState: state,
        action,
        newState
      })

      return {
        ...newState
      }
    }

    case CACHE_DELETE_FLOW_TASK_INTERACTION_CHECK: {
      const newState = _removeFlowGroupTaskInstanceCheck(
        state,
        action.data.flowGroup,
        action.data.tid,
        action.data.checkId
      )

      Logger.trace(LoggerTypes.REDUCER, '[REDUCER] flowGroups.CACHE_DELETE_FLOW_TASK_INTERACTION_CHECK()', {
        oldState: state,
        action,
        newState
      })

      return {
        ...newState
      }
    }

    case CACHE_CREATE_FLOW_TASK_INTERACTION_RESOURCE: {
      const newState = _addFlowGroupTaskInstanceResourceInteraction(
        state,
        action.data.flowGroup,
        action.data.tid,
        action.data.interactions
      )

      Logger.trace(LoggerTypes.REDUCER, '[REDUCER] flowGroups.CACHE_CREATE_FLOW_TASK_INTERACTION_RESOURCE()', {
        oldState: state,
        action,
        newState
      })

      return newState
    }

    case CACHE_UPDATE_FLOW_TASK_INTERACTION_RESOURCE: {
      const newState = _updateFlowGroupTaskInstanceResourceInteraction(
        state,
        action.data.flowGroup,
        action.data.tid,
        action.data.interaction
      )

      Logger.trace(LoggerTypes.REDUCER, '[REDUCER] flowGroups.CACHE_UPDATE_FLOW_TASK_INTERACTION_RESOURCE()', {
        oldState: state,
        action,
        newState
      })

      return newState
    }

    case CACHE_DELETE_FLOW_TASK_INTERACTION_RESOURCE: {
      const newState = _removeFlowGroupTaskInstanceResourceInteraction(
        state,
        action.data.flowGroup,
        action.data.tid,
        action.data.resourceId
      )

      Logger.trace(LoggerTypes.REDUCER, '[REDUCER] flowGroups.CACHE_DELETE_FLOW_TASK_INTERACTION_RESOURCE()', {
        oldState: state,
        action,
        newState
      })

      return newState
    }

    case CACHE_CREATE_FLOW_TASK_INTERACTION_VARIABLE: {
      const newState = _addFlowGroupTaskInstanceVariableInteraction(
        state,
        action.data.flowGroup,
        action.data.tid,
        action.data.interactions
      )

      Logger.trace(LoggerTypes.REDUCER, '[REDUCER] flowGroups.CACHE_CREATE_FLOW_TASK_INTERACTION_VARIABLE()', {
        oldState: state,
        action,
        newState
      })

      return newState
    }

    case CACHE_UPDATE_FLOW_TASK_INTERACTION_VARIABLE: {
      const newState = _updateFlowGroupTaskInstanceVariableInteraction(
        state,
        action.data.flowGroup,
        action.data.tid,
        action.data.interaction
      )

      Logger.trace(LoggerTypes.REDUCER, '[REDUCER] flowGroups.CACHE_UPDATE_FLOW_TASK_INTERACTION_VARIABLE()', {
        oldState: state,
        action,
        newState
      })

      return newState
    }

    case CACHE_DELETE_FLOW_TASK_INTERACTION_VARIABLE: {
      const newState = _removeFlowGroupTaskInstanceVariableInteraction(
        state,
        action.data.flowGroup,
        action.data.tid,
        action.data.interaction
      )

      Logger.trace(LoggerTypes.REDUCER, '[REDUCER] flowGroups.CACHE_DELETE_FLOW_TASK_INTERACTION_VARIABLE()', {
        oldState: state,
        action,
        newState
      })

      return newState
    }

    default:
      return state
  }
}
